/**
 * TAMS utils
 * TAMS Common 유틸함수
 *
 * @since 2021-01-21
 * @author mk.ko
 */

/**
 * 연속된 문자열 체크 함수 (ASCII CODE 기반)
 * ---
 * @param str 체크할 문자열
 * @param limit 연속 입력 제한값
 * ---
 * c: 현재 문자 아스키 코드
 * o: c의 이전 상태 백업(이전문자의 아스키 코드)
 * p: c와 o의 아스키 코드 차이값 (이 값이 -1, 0, 1이면 이웃한 두 문자가 연속)
 * d: p의 이전 상태 (백업 p와 d가 동일하다면 연속이 계속되므로 카운트)
 * n: p와 d가 같을 때마다 카운트, p와 d가 다르면 0으로 초기화
 * l: 연속 입력 가능 제한값
 * ---
 * @returns {boolean}
 */
const stck = (str, limit = 3) => {
  const l = limit
  let o, d, p, n = 0
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i)
    const cond = (p = o - c) > -2 && p < 2 && (n = p === d ? n + 1 : 0)
    if (i > 0 && cond > l - 3) {
      return false
    }
    d = p
    o = c
  }
  return true
}

const getRefPath = (target, path = []) => {
  let ref = target
  try {
    if (!(path.length > 0)) return ref
    for (let j = 0; j < path.length; j++) {
      const idx = path[j]
      if (typeof idx !== 'undefined' && typeof ref[idx] !== 'undefined') {
        ref = ref[idx]
      }
    }
  } catch (e) {
    window.$nuxt.$log('[Common Utils GUI Errror (getRefPath)]', e)
  }
  return ref
}

// TAMS
const compareList = (original, target, targetKeys = []) => {
  try {
    if (
      _.get(original, ['length'], -1) === -1 ||
      _.get(target, ['length'], -1) === -1
    ) {
      return false
    }
    if (_.get(original, ['length'], 0) !== _.get(target, ['length'], 0)) {
      return false
    }

    const isExistTargetKeys = _.get(targetKeys, ['length'], 0) > 0

    let res = true
    original.forEach((item, idx) => {
      const targetItem = target[idx]
      const keys = isExistTargetKeys ? targetKeys : Object.keys(item)
      for (const key of keys) {
        if (item[key] !== targetItem[key]) {
          res = false
          return false
        }
      }
    })

    return res
  } catch (e) {
    window.$nuxt.$log('[Utils Comapre GUI Error (compareList)]', e)
    return false
  }
}

const parseJSON = (json, defaultValue = {}) => {
  try {
    return JSON.parse(json)
  } catch (e) {
    window.$nuxt.$log('[Utils Comapre GUI Error (parseJSON)]', e)
  }
  return defaultValue
}

/**
 * object 리스트 추가 변수 mapping
 * @param list 리스트
 * @param mapData 추가변수 정보
 * @returns {string|any}
 */
const addDatasToList = (list, mapData) => {
  try {
    _.map(list, (data) => {
      _.forEach(Object.keys(mapData), (key) => {
        data[key] = mapData[key]
      })
      return data
    })
  } catch (e) {
    window.$nuxt.$log('[Utils Comapre GUI Error (mapListDatas)]', e)
  }
  return list
}

/**
 * 리스트에서 값에 따른 label값 리턴
 * @param list 리스트
 * @param value 특정 값
 * @returns {*|string}
 */
const findValueLabel = (list, value) => {
  const data = _.find(list, { value: value })
  return data ? data.label : ''
}

/**
 * 두 배열을 index mapping된 객체로 변환
 * @param keyList key list ex) ['a','b']
 * @param valueList valueList list ex) [[1,2], [3,4]]
 */
const arraysIntoObject = (keyList, valueList) => {
  if (keyList.length !== (valueList[0] && valueList[0].length)) return []
  const list = _.reduce(valueList, (result, rule) => {
    const item = keyList.reduce((res, key, idx) => ({...res, [key]: rule[idx]}), {})
    result.push(item)
    return result
  }, [])
  return list
}

const getDeviceGroupType = () => {
  // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
  const deviceGroupType = [0, 4, 7, 8]
  return deviceGroupType
}

// device condition checker
const isDeviceGroupType = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceGroupType = getDeviceGroupType()
    let deviceType
    if (typeof item === 'number') { // 하위 버전 기능 호환 지원용 (item.device_type)
      deviceType = item
    } else {
      deviceType = _.get(item, [key])
    }
    return deviceGroupType.indexOf(deviceType) !== -1
  } catch (e) {
    window.$nuxt.$log('[Device Tree Component GUI Error (isDeviceGroupType)]', e)
    return false
  }
}

const isRootNode = (item, key = 'device_id') => {
  try {
    return _.get(item, [key]) === 1
  } catch (e) {
    return false
  }
}

const isTamsNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceType = _.get(item, [key])
    return (deviceType === 5 || deviceType === 6)
  } catch (e) {
    return false
  }
}

const isTamsMasterNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceType = _.get(item, [key])
    return deviceType === 5
  } catch (e) {
    return false
  }
}

const isTamsSlaveNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceType = _.get(item, [key])
    return deviceType === 6
  } catch (e) {
    return false
  }
}

const isTamsGroupNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceType = _.get(item, [key])
    return (deviceType === 4)
  } catch (e) {
    return false
  }
}

// 하위 TAMS 내 등록된 모든 관리 장비
const isSubTamsNode = (item, key = 'device_sub_type') => {
  try {
    return (_.get(item, [key]) === 1) // 1: 하위, 0: 기본
  } catch (e) {
    return false
  }
}

const isSubTamsRootNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceType = _.get(item, [key])
    return (deviceType === 7)
  } catch (e) {
    return false
  }
}

const isSubTamsGroupNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const
      deviceType = _.get(item, [key]),
      subTamsGroupNodes = [7, 8]

    return subTamsGroupNodes.indexOf(deviceType) !== -1
  } catch (e) {
    return false
  }
}

const isSubTamsMasterNode = (item, deviceTypeKey = 'device_type', deviceSubKey = 'device_sub_type') => {
  try {
    const
      deviceType = _.get(item, [deviceTypeKey]), // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
      deviceSubType = _.get(item, [deviceSubKey]) // 1: 하위, 0: 기본

    return deviceSubType === 1 && deviceType === 5
  } catch (e) {
    return false
  }
}

const isSlaveNode = (item, key = 'device_type') => {
  try {
    // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
    const deviceType = _.get(item, [key])
    return deviceType === 3 || deviceType === 6
  } catch (e) {
    return false
  }
}

const isMonNode = (item, key = 'device_mng_type') => {
  try {
    // 1: Config Mode, 2: View Mode, 3: Monitoring Node
    const deviceMngType = _.get(item, [key])
    return deviceMngType === 3
  } catch (e) {
    return false
  }
}

const isMfxSeries = (item, key = 'device_model_type') => {
  try {
    // device_model_type
    // 0: TAMS, 9: MF2, 10: MFI, 11: MFD, 12: MF2 AE, 20: NGF
    const
      mfxSeriesCodes = [9, 10, 11, 12],
      deviceModelType = _.get(item, [key])

    return mfxSeriesCodes.indexOf(deviceModelType) !== -1
  } catch (e) {
    return false
  }
}

const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export {
  stck,
  getRefPath,
  compareList,
  parseJSON,
  addDatasToList,
  findValueLabel,
  arraysIntoObject,
  sleep,

  // device condition checker
  getDeviceGroupType,
  isDeviceGroupType,
  isRootNode,
  isTamsNode,
  isTamsMasterNode,
  isTamsSlaveNode,
  isTamsGroupNode,
  isSubTamsNode,
  isSubTamsRootNode,
  isSubTamsGroupNode,
  isSubTamsMasterNode,
  isSlaveNode,
  isMonNode,
  isMfxSeries
}
